import React, { useState, useContext, useEffect, useCallback } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import FieldConfigurationAPI from "../../../../../lib/api/FieldConfiguration";
import DetailHeader from "../../components/DetailHeader";
import LicenseErrorModal from "../../components/LicenseErrorModal";
import FormFooter from "../../components/FormFooter";
import Validation from "../../../../common/Validation";
import { isValidRoutingNumber } from "../../../../../lib/RoutingNumber";
import {
  formatPhoneNumber,
  isValidPhoneNumber,
} from "../../../../../lib/PhoneNumber";
import PerCompanyConfig from "./PerCompanyConfig";
import "./style.css";

// Shape ExpenseAch expects. Used both as the immediate default for a
// company-user that doesn't exist yet (nothing to fetch) and for a fetched
// result with no vault record on file.
const blankAch = (data) => ({
  hasRecord: !!data,
  accountLastFour: data?.account_last_four || "",
  routingLastFour: data?.routing_last_four || "",
  accountType: data?.account_type || "checking",
  prenote: !!data?.prenote,
  enabled: data ? data.enabled !== false : false,
  // The vault never returns routing/account numbers — these start
  // empty and a non-empty value always means "admin typed a new one".
  routingNumber: "",
  accountNumber: "",
});

export default function UserForm({ user, onSave, onCancel }) {
  const {
    createUser,
    updateUser,
    updateAchStatus,
    fetchLicenses,
    fetchGroupsForCompany,
    fetchRoles,
    companies,
    roles,
    users,
    licenses,
  } = useContext(FieldConfigurationContext);

  const isEditMode = !!user;
  const configureUsersData = companies?.configureUsersData || {};
  const manageableUuidsList = Object.keys(configureUsersData);
  const showCompanySection = manageableUuidsList.length > 1;

  // Personal info state
  const [personalInfo, setPersonalInfo] = useState({
    email: user?.email || "",
    mobileNumber: formatPhoneNumber(user?.mobileNumber),
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    address: user?.address || "",
    city: user?.city || "",
    state: user?.state || "",
    zip: user?.zip || "",
  });

  // Per-company configuration state
  // { [companyUuid]: { assigned, userUuid, employee, roleUuid, groups, enabled, availableGroups, groupsLoading } }
  const [companyConfigs, setCompanyConfigs] = useState(() => {
    const configs = {};
    for (const companyUuid of manageableUuidsList) {
      const userIdx = user?.companyUuids?.indexOf(companyUuid) ?? -1;
      const existingUserUuid = userIdx >= 0 ? user?.uuids?.[userIdx] : null;

      // Find the individual user record for this company
      const companyUserRecord =
        existingUserUuid && users?.data
          ? users.data.find((u) => u.uuid === existingUserUuid)
          : null;

      // originalRoleUuid/originalEnabled snapshot the pre-edit state so the license
      // pre-check can tell "role changed to add a capability" from "role unchanged".
      const originalRoleUuid =
        companyUserRecord?.roleUuid || companyUserRecord?.role || "";
      configs[companyUuid] = {
        assigned: !!existingUserUuid,
        userUuid: existingUserUuid || null,
        employee: companyUserRecord?.employee || companyUserRecord?.code || "",
        roleUuid: originalRoleUuid,
        originalRoleUuid,
        originalEnabled: companyUserRecord?.active !== false,
        groups: companyUserRecord?.groups || [],
        enabled: companyUserRecord?.active !== false,
        availableGroups: [],
        groupsLoading: false,
        cards: [],
        originalCardUuids: [],
        cardsLoading: false,
        // null until loadAchForCompany resolves — distinct from "loaded, no
        // record" so the section doesn't flash a false "not set up" state.
        // No point fetching for a company-user that doesn't exist yet
        // (new user, or an existing user newly assigned to this company) —
        // default straight to a blank record instead of waiting forever.
        ach: existingUserUuid ? null : blankAch(),
        achLoading: false,
      };
    }
    // Single-company mode: always assigned
    if (!showCompanySection && manageableUuidsList.length === 1) {
      const companyUuid = manageableUuidsList[0];
      configs[companyUuid] = {
        ...configs[companyUuid],
        assigned: true,
      };
    }
    return configs;
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [licenseErrors, setLicenseErrors] = useState([]);
  const [licenseErrorDetails, setLicenseErrorDetails] = useState([]);
  const [licenseErrorMessage, setLicenseErrorMessage] = useState("");
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseErrorCompanyUuids, setLicenseErrorCompanyUuids] = useState([]);

  // Load groups for a single company
  const loadGroupsForCompany = useCallback(
    async (companyUuid) => {
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: { ...prev[companyUuid], groupsLoading: true },
      }));
      const result = await fetchGroupsForCompany(companyUuid);
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: {
          ...prev[companyUuid],
          availableGroups: (result.data || []).filter(
            (g) => (g.group_type || "app") === "app"
          ),
          groupsLoading: false,
        },
      }));
    },
    [fetchGroupsForCompany]
  );

  // Load an existing user's expense cards for a single company
  const loadCardsForCompany = useCallback(
    async (companyUuid, companyUserUuid) => {
      if (!companyUserUuid) return;
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: { ...prev[companyUuid], cardsLoading: true },
      }));
      const result = await FieldConfigurationAPI.getExpenseCards(
        companyUserUuid,
        companyUuid
      );
      const cards = (result.data || []).map((c) => ({
        uuid: c.uuid,
        description: c.name || "",
        glAccountCode: c.gl_account_code || "",
      }));
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: {
          ...prev[companyUuid],
          cards,
          originalCardUuids: cards.map((c) => c.uuid),
          cardsLoading: false,
        },
      }));
    },
    []
  );

  // Load an existing user's ACH info for a single company. Mirrors
  // loadCardsForCompany — fetched whenever the admin might be able to manage
  // ACH for this company (companies.manageAchData, already loaded by the
  // time this form mounts), independent of the currently-selected role.
  // *Rendering* the section is what's actually gated on role.expenses (see
  // PerCompanyConfig/ExpenseAch) — gating the fetch on the role too would
  // race against roles still loading (fetchRoles below runs separately).
  const loadAchForCompany = useCallback(
    async (companyUuid, companyUserUuid) => {
      if (!companyUserUuid) return;
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: { ...prev[companyUuid], achLoading: true },
      }));
      const result = await FieldConfigurationAPI.getUserAch(
        companyUserUuid,
        companyUuid
      );
      const data = result.status === 200 ? result.data : null;
      const ach = blankAch(data);
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: { ...prev[companyUuid], ach, achLoading: false },
      }));
    },
    []
  );

  // Load groups (and, for already-assigned users, expense cards/ACH info) for
  // all assigned companies on mount
  useEffect(() => {
    for (const [companyUuid, config] of Object.entries(companyConfigs)) {
      if (config.assigned) {
        loadGroupsForCompany(companyUuid);
        if (config.userUuid) {
          loadCardsForCompany(companyUuid, config.userUuid);
          if (companies?.manageAchData?.[companyUuid]) {
            loadAchForCompany(companyUuid, config.userUuid);
          }
        }
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Roles are otherwise only loaded when RolesList/UsersList happens to have
  // mounted first — fetch (or reuse the cache) here so the Role dropdown and
  // the license pre-check aren't empty just because the admin navigated
  // straight to Users > Add/Edit.
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  // Mirrors CE: picking an employee fills the personal fields that have data.
  const handleEmployeeSelect = (employee) => {
    const fill = {
      firstName: employee.first_name,
      lastName: employee.last_name,
      email: employee.email,
      mobileNumber: formatPhoneNumber(employee.phone),
      address: employee.address,
      city: employee.city,
      state: employee.state,
      zip: employee.zip || employee.zipcode,
    };
    setPersonalInfo((prev) => {
      const next = { ...prev };
      Object.entries(fill).forEach(([key, val]) => {
        if (val) next[key] = val;
      });
      return next;
    });
  };

  const handleCompanyToggle = (companyUuid) => {
    // Read from state, not inside the updater: updaters may run lazily.
    const existing = companyConfigs[companyUuid];
    const shouldLoadGroups =
      !existing.assigned &&
      existing.availableGroups.length === 0 &&
      !existing.groupsLoading;

    setCompanyConfigs((prev) => {
      const current = prev[companyUuid];
      if (current.assigned) {
        // Already assigned — toggle active/inactive instead of removing
        return {
          ...prev,
          [companyUuid]: { ...current, enabled: current.enabled === false },
        };
      }
      // Not yet assigned — assign it
      return { ...prev, [companyUuid]: { ...current, assigned: true } };
    });

    if (shouldLoadGroups) {
      loadGroupsForCompany(companyUuid);
    }
  };

  const handleCompanyConfigChange = (companyUuid, field, value) => {
    setCompanyConfigs((prev) => {
      const current = prev[companyUuid];
      let next = { ...current, [field]: value };
      // Switching to a non-expense role hides the ACH section — clear any
      // unsaved routing/account number rather than leave it sitting in
      // memory behind a hidden field (plan §8 behavior 1).
      if (field === "roleUuid" && current.ach) {
        const newRole = roles?.data?.find((r) => r.uuid === value);
        if (newRole?.expenses !== true) {
          next = {
            ...next,
            ach: { ...current.ach, routingNumber: "", accountNumber: "" },
          };
        }
      }
      return { ...prev, [companyUuid]: next };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!personalInfo.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalInfo.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!personalInfo.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!personalInfo.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (
      personalInfo.mobileNumber &&
      !isValidPhoneNumber(personalInfo.mobileNumber)
    ) {
      newErrors.mobileNumber = "Enter a 10-digit phone number";
    }
    if (
      personalInfo.zip?.trim() &&
      !/^\d{5}(-\d{4})?$/.test(personalInfo.zip.trim())
    ) {
      newErrors.zip = "Enter a valid zip code (12345 or 12345-6789)";
    }

    const assignedCompanies = Object.entries(companyConfigs).filter(
      ([, cfg]) => cfg.assigned
    );
    if (assignedCompanies.length === 0) {
      newErrors.companies = "Please assign the user to at least one company.";
    }
    for (const [companyUuid, cfg] of assignedCompanies) {
      if (!cfg.roleUuid) {
        newErrors[`role_${companyUuid}`] = "Role is required";
      }
      // CE Desktop requires at least one access group per assigned company.
      // Skip this when the company has none configured yet — nothing to pick.
      if (
        (cfg.groups || []).length === 0 &&
        (cfg.availableGroups || []).length > 0
      ) {
        newErrors[`groups_${companyUuid}`] = "Access Group is required";
      }
      // Only validate routing/account number when a new secret is actually
      // being submitted (a brand-new record, or the admin retyped the
      // account number) — an existing enabled record with only Account
      // Type/Prenote changed never repopulates these fields (plan §8
      // behavior 4) and shouldn't be forced through this check.
      if (cfg.ach?.enabled && (!cfg.ach.hasRecord || cfg.ach.accountNumber)) {
        if (!isValidRoutingNumber(cfg.ach.routingNumber)) {
          newErrors[`ach_routing_${companyUuid}`] =
            "Not a valid bank routing number.";
        }
        if (!cfg.ach.accountNumber) {
          newErrors[`ach_account_${companyUuid}`] =
            "Account number is required.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      setError("Please fix the errors before saving.");
      return;
    }

    setSaving(true);
    setError("");
    setLicenseErrors([]);
    setLicenseErrorDetails([]);
    setLicenseErrorCompanyUuids([]);
    setSuccess("");

    const assignedEntries = Object.entries(companyConfigs).filter(
      ([, cfg]) => cfg.assigned
    );
    const deactivateEntries = Object.entries(companyConfigs).filter(
      ([, cfg]) => !cfg.assigned && cfg.userUuid
    );

    // Pre-check license availability for every assignment that will be active after
    // saving — covers brand-new assignments, role changes that add a capability the
    // user's previous role didn't have, and re-enabling a previously disabled user.
    // The backend does not currently enforce this on update (only on create), so this
    // is the only place Edit User's license check happens.
    const resolveRole = (companyUuid, roleUuid) => {
      if (!roleUuid) return null;
      let role = roles.data.find(
        (r) => r.company_uuid === companyUuid && r.uuid === roleUuid
      );
      if (!role) {
        const roleName = roles.data.find((r) => r.uuid === roleUuid)?.name;
        if (roleName) {
          role = roles.data.find(
            (r) => r.company_uuid === companyUuid && r.name === roleName
          );
        }
      }
      return role || null;
    };

    const licenseImpactedAssignments = assignedEntries.filter(
      ([, cfg]) => cfg.enabled !== false
    );
    const licenseImpactedCompanyUuids = [];

    // Licenses are otherwise only loaded when the Licenses tab has been visited —
    // fetch (or reuse the cache) here so the check below isn't silently skipped
    // just because the admin navigated straight to Users > Add/Edit.
    let licData = licenses?.data;
    if (licenseImpactedAssignments.length > 0) {
      const licenseFetch = await fetchLicenses();
      if (licenseFetch.success) {
        licData = licenseFetch.data;
      }
    }

    if (licenseImpactedAssignments.length > 0 && licData) {
      let timeNeeded = 0,
        expenseNeeded = 0,
        projectNeeded = 0,
        serviceNeeded = 0;

      for (const [companyUuid, cfg] of licenseImpactedAssignments) {
        const newRole = resolveRole(companyUuid, cfg.roleUuid);
        if (!newRole) continue;

        // If this company_user was already active, the old role's capabilities are
        // already counted in licenses.data — only a newly-gained capability adds load.
        const wasActive = !!cfg.userUuid && cfg.originalEnabled !== false;
        const oldRole = wasActive
          ? resolveRole(companyUuid, cfg.originalRoleUuid)
          : null;

        const gainsTime = newRole.enableTime && !oldRole?.enableTime;
        const gainsExpense = newRole.enableExpense && !oldRole?.enableExpense;
        const gainsProject =
          newRole.enableProjectManagement && !oldRole?.enableProjectManagement;
        const gainsService =
          newRole.enableServiceManagement && !oldRole?.enableServiceManagement;

        if (gainsTime) timeNeeded++;
        if (gainsExpense) expenseNeeded++;
        if (gainsProject) projectNeeded++;
        if (gainsService) serviceNeeded++;
        if (gainsTime || gainsExpense || gainsProject || gainsService) {
          licenseImpactedCompanyUuids.push(companyUuid);
        }
      }

      const violations = [];
      if (timeNeeded > 0 && licData.time?.available < timeNeeded) {
        violations.push({
          name: "Time Entry",
          used: licData.time.used ?? 0,
          total: licData.time.limit ?? 0,
          available: licData.time.available ?? 0,
          needed: timeNeeded,
        });
      }
      if (expenseNeeded > 0 && licData.expenses?.available < expenseNeeded) {
        violations.push({
          name: "Expense",
          used: licData.expenses.used ?? 0,
          total: licData.expenses.limit ?? 0,
          available: licData.expenses.available ?? 0,
          needed: expenseNeeded,
        });
      }
      if (
        projectNeeded > 0 &&
        licData.projectManagement?.available < projectNeeded
      ) {
        violations.push({
          name: "Project Management",
          used: licData.projectManagement.used ?? 0,
          total: licData.projectManagement.limit ?? 0,
          available: licData.projectManagement.available ?? 0,
          needed: projectNeeded,
        });
      }
      if (
        serviceNeeded > 0 &&
        licData.serviceManagement?.available < serviceNeeded
      ) {
        violations.push({
          name: "Service Management",
          used: licData.serviceManagement.used ?? 0,
          total: licData.serviceManagement.limit ?? 0,
          available: licData.serviceManagement.available ?? 0,
          needed: serviceNeeded,
        });
      }

      if (violations.length > 0) {
        const impactedCompanyNames = licenseImpactedCompanyUuids.map(
          (uuid) => configureUsersData[uuid] || uuid
        );
        const companyClause =
          impactedCompanyNames.length > 0
            ? ` for ${impactedCompanyNames.join(", ")}`
            : "";
        setLicenseErrorMessage(
          `This user can't be added to the selected role${companyClause} because one or more enabled capabilities in the role have reached their license limit.`
        );
        setLicenseErrors([]);
        setLicenseErrorDetails(violations);
        setLicenseErrorCompanyUuids(licenseImpactedCompanyUuids);
        setShowLicenseModal(true);
        setSaving(false);
        return;
      }
    }

    let hasLicenseError = false;
    let firstLicenseError = null;
    let firstLicenseErrorCompanyUuid = null;
    let anyFailure = false;

    const isAdminScope = sessionStorage.getItem("field_token_scope") === "user";

    try {
      // Process assigned companies (create or update)
      for (const [companyUuid, cfg] of assignedEntries) {
        // Resolve role UUID for this specific company — the stored UUID may belong to a
        // different company (same role name, different UUID). Match by name if needed.
        let resolvedRoleUuid = cfg.roleUuid;
        const roleInCompany = roles.data.find(
          (r) => r.company_uuid === companyUuid && r.uuid === resolvedRoleUuid
        );
        if (!roleInCompany) {
          const roleName = roles.data.find(
            (r) => r.uuid === resolvedRoleUuid
          )?.name;
          if (roleName) {
            const matched = roles.data.find(
              (r) => r.company_uuid === companyUuid && r.name === roleName
            );
            resolvedRoleUuid = matched?.uuid || resolvedRoleUuid;
          }
        }

        const userData = {
          email: personalInfo.email,
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          mobileNumber: personalInfo.mobileNumber,
          address: personalInfo.address,
          city: personalInfo.city,
          state: personalInfo.state,
          zip: personalInfo.zip,
          employee: cfg.employee,
          role: resolvedRoleUuid,
          active: cfg.enabled,
          groups: cfg.groups,
        };

        // Always pass companyUuid for admin (user-scoped) tokens so the API validates
        // against the correct managed company, not the admin's own company.
        const targetCompany = isAdminScope ? companyUuid : null;

        let result;
        if (cfg.userUuid) {
          result = await updateUser(cfg.userUuid, userData, targetCompany);
        } else {
          result = await createUser(userData, targetCompany);
        }

        if (!result.success) {
          if (result.status === 403 && result.errors?.length > 0) {
            hasLicenseError = true;
            firstLicenseError = result;
            firstLicenseErrorCompanyUuid = companyUuid;
            break;
          } else {
            anyFailure = true;
            setError(
              result.error ||
                `Failed to ${cfg.userUuid ? "update" : "create"} user for ${
                  configureUsersData[companyUuid] || companyUuid
                }.`
            );
            break;
          }
        }

        // Create/update/delete this company's expense cards against the
        // company_user_uuid we just resolved (brand-new for a create).
        const companyUserUuid = result.data?.uuid || cfg.userUuid;
        if (companyUserUuid) {
          const removedCardUuids = cfg.originalCardUuids.filter(
            (uuid) => !cfg.cards.some((c) => c.uuid === uuid)
          );
          for (const cardUuid of removedCardUuids) {
            await FieldConfigurationAPI.deleteExpenseCard(
              cardUuid,
              targetCompany
            );
          }
          for (const card of cfg.cards) {
            if (!card.description?.trim()) continue;
            if (card.uuid) {
              await FieldConfigurationAPI.updateExpenseCard(
                card.uuid,
                {
                  name: card.description,
                  gl_account_code: card.glAccountCode || "",
                },
                targetCompany
              );
            } else {
              await FieldConfigurationAPI.createExpenseCard(
                {
                  company_user_uuid: companyUserUuid,
                  name: card.description,
                  gl_account_code: card.glAccountCode || "",
                },
                targetCompany
              );
            }
          }

          // ACH: single record per company_user, not a list like cards.
          // `ach` is null when the section was never rendered/loaded for
          // this company — nothing to save in that case.
          const ach = cfg.ach;
          if (ach) {
            let achMutated = false;
            if (!ach.enabled && ach.hasRecord) {
              await FieldConfigurationAPI.deleteUserAch(
                companyUserUuid,
                targetCompany
              );
              achMutated = true;
            } else if (ach.enabled && ach.accountNumber) {
              // A non-empty account number always means a newly-typed secret
              // — the masked field never round-trips the real value, so this
              // can't be "unchanged" (plan §8 behavior 4).
              await FieldConfigurationAPI.saveUserAch(
                companyUserUuid,
                {
                  routingNumber: ach.routingNumber,
                  accountNumber: ach.accountNumber,
                  accountType: ach.accountType,
                  prenote: ach.prenote,
                },
                targetCompany
              );
              achMutated = true;
            } else if (ach.enabled && ach.hasRecord) {
              // Only attributes may have changed (Account Type / Prenote) —
              // don't force a re-type of the bank numbers to flip Prenote.
              await FieldConfigurationAPI.saveUserAchAttributes(
                companyUserUuid,
                { accountType: ach.accountType, prenote: ach.prenote },
                targetCompany
              );
              achMutated = true;
            }
            // ach.enabled && !ach.hasRecord && !ach.accountNumber: a new
            // record with no bank numbers entered — surfaced as a field
            // validation error before save (see ExpenseAch.js), not silently
            // skipped here.

            // Non-secret mirror on api_identity, so read-only views can show
            // ACH status without a vault call. Only written when a vault
            // mutation actually ran above — never a standalone settable flag.
            if (achMutated) {
              await updateAchStatus(
                companyUserUuid,
                {
                  payExpensesViaAch: ach.enabled,
                  achUpdatedAt: new Date().toISOString(),
                },
                targetCompany
              );
            }
          }
        }
      }

      if (!anyFailure) {
        // Deactivate removed companies
        for (const [companyUuid, cfg] of deactivateEntries) {
          await updateUser(
            cfg.userUuid,
            { active: false },
            isAdminScope ? companyUuid : null
          );
        }
      }

      if (hasLicenseError && firstLicenseError) {
        const failedCompanyName = firstLicenseErrorCompanyUuid
          ? configureUsersData[firstLicenseErrorCompanyUuid] ||
            firstLicenseErrorCompanyUuid
          : null;
        const companyClause = failedCompanyName
          ? ` for ${failedCompanyName}`
          : "";
        setLicenseErrorMessage(
          firstLicenseError.error ||
            `This user can't be added to the selected role${companyClause} because one or more enabled capabilities in the role have reached their license limit.`
        );
        setLicenseErrors(firstLicenseError.errors || []);
        setLicenseErrorDetails(firstLicenseError.details || []);
        setLicenseErrorCompanyUuids(
          firstLicenseErrorCompanyUuid ? [firstLicenseErrorCompanyUuid] : []
        );
        setShowLicenseModal(true);
      } else if (!anyFailure) {
        setSuccess(`User ${isEditMode ? "updated" : "created"} successfully!`);
        setTimeout(() => {
          onSave();
        }, 1000);
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel?")) {
      onCancel();
    }
  };

  const assignedCompanyUuids = Object.entries(companyConfigs)
    .filter(([, cfg]) => cfg.assigned)
    .map(([uuid]) => uuid);

  return (
    <div className="user-form">
      <DetailHeader
        title={isEditMode ? "Edit User" : "Add User"}
        onBack={handleCancel}
        isEditing={false}
      />

      {error && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      <LicenseErrorModal
        isOpen={showLicenseModal}
        onClose={async () => {
          setShowLicenseModal(false);
          await fetchLicenses(true);
        }}
        errorMessage={licenseErrorMessage}
        details={licenseErrorDetails}
        context="user"
        companyUuids={licenseErrorCompanyUuids}
      />

      <div className="user-form__two-col">
        {/* Left Panel */}
        <div className="user-form__left">
          <div className="user-form__section">
            <h3 className="user-form__section-title">Personal Information</h3>
            <div className="user-form__fields">
              <div className="user-form__name-row">
                <div className="user-form__field">
                  <label className="user-form__label">
                    First Name <span className="danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={personalInfo.firstName}
                    onChange={(e) =>
                      handlePersonalInfoChange("firstName", e.target.value)
                    }
                    placeholder="First name"
                    className={`user-form__input${
                      errors.firstName ? " input-error" : ""
                    }`}
                  />
                  {errors.firstName && (
                    <span className="user-form__error">{errors.firstName}</span>
                  )}
                </div>

                <div className="user-form__field">
                  <label className="user-form__label">
                    Last Name <span className="danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={personalInfo.lastName}
                    onChange={(e) =>
                      handlePersonalInfoChange("lastName", e.target.value)
                    }
                    placeholder="Last name"
                    className={`user-form__input${
                      errors.lastName ? " input-error" : ""
                    }`}
                  />
                  {errors.lastName && (
                    <span className="user-form__error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="user-form__field">
                <label className="user-form__label">
                  Email <span className="danger">*</span>
                </label>
                <input
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) =>
                    handlePersonalInfoChange("email", e.target.value)
                  }
                  placeholder="email@example.com"
                  className={`user-form__input${
                    errors.email ? " input-error" : ""
                  }`}
                />
                {errors.email && (
                  <span className="user-form__error">{errors.email}</span>
                )}
              </div>

              <div className="user-form__field">
                <label className="user-form__label">Mobile Number</label>
                <input
                  type="tel"
                  value={personalInfo.mobileNumber}
                  onChange={(e) =>
                    handlePersonalInfoChange(
                      "mobileNumber",
                      formatPhoneNumber(e.target.value)
                    )
                  }
                  maxLength={14}
                  placeholder="(XXX) XXX-XXXX"
                  className={`user-form__input${
                    errors.mobileNumber ? " input-error" : ""
                  }`}
                />
                {errors.mobileNumber && (
                  <span className="user-form__error">
                    {errors.mobileNumber}
                  </span>
                )}
              </div>

              <div className="user-form__field">
                <label className="user-form__label">Address</label>
                <input
                  type="text"
                  value={personalInfo.address}
                  onChange={(e) =>
                    handlePersonalInfoChange("address", e.target.value)
                  }
                  placeholder="Street address"
                  className="user-form__input"
                />
              </div>

              <div className="user-form__address-row">
                <input
                  type="text"
                  value={personalInfo.city}
                  onChange={(e) =>
                    handlePersonalInfoChange("city", e.target.value)
                  }
                  placeholder="City"
                  className="user-form__input user-form__input--city"
                />
                <input
                  type="text"
                  value={personalInfo.state}
                  onChange={(e) =>
                    handlePersonalInfoChange("state", e.target.value)
                  }
                  placeholder="ST"
                  maxLength={2}
                  className="user-form__input user-form__input--state"
                />
                <input
                  type="text"
                  value={personalInfo.zip}
                  onChange={(e) =>
                    handlePersonalInfoChange("zip", e.target.value)
                  }
                  placeholder="Zip"
                  maxLength={10}
                  className={`user-form__input user-form__input--zip${
                    errors.zip ? " input-error" : ""
                  }`}
                />
              </div>
              {errors.zip && (
                <span className="user-form__error">{errors.zip}</span>
              )}
            </div>
          </div>

          {showCompanySection && (
            <div className="user-form__section">
              <h3 className="user-form__section-title">Companies</h3>
              <div className="user-form__company-badges">
                {Object.entries(configureUsersData).map(([uuid, name]) => {
                  const isAssigned = companyConfigs[uuid]?.assigned;
                  const isActive = companyConfigs[uuid]?.enabled !== false;
                  const badgeClass = isAssigned
                    ? isActive
                      ? " user-form__company-badge--assigned"
                      : " user-form__company-badge--assigned-inactive"
                    : "";
                  const code = companies?.codesMap?.[uuid];
                  return (
                    <button
                      key={uuid}
                      type="button"
                      className={`user-form__company-badge${badgeClass}`}
                      onClick={() => handleCompanyToggle(uuid)}
                      disabled={saving}
                    >
                      {code != null ? `${name} (${code})` : name}
                    </button>
                  );
                })}
              </div>
              {errors.companies && (
                <span
                  className="user-form__error"
                  style={{ marginTop: "8px", display: "block" }}
                >
                  {errors.companies}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="user-form__right">
          <h3 className="user-form__panel-title">Per-Company Configuration</h3>
          {assignedCompanyUuids.length === 0 ? (
            <div className="user-form__empty-state">
              {manageableUuidsList.length === 0
                ? "No companies are available. Reload the page, or contact support if this persists."
                : "Select a company on the left to configure access."}
            </div>
          ) : (
            assignedCompanyUuids.map((companyUuid) => {
              const companyName = configureUsersData[companyUuid];
              const code = companies?.codesMap?.[companyUuid];
              const cfg = companyConfigs[companyUuid];
              const companyRoles = roles.data.filter(
                (r) => r.company_uuid === companyUuid
              );
              return (
                <PerCompanyConfig
                  key={companyUuid}
                  companyUuid={companyUuid}
                  companyName={companyName}
                  companyCode={code}
                  config={cfg}
                  availableRoles={companyRoles}
                  onChange={(field, value) =>
                    handleCompanyConfigChange(companyUuid, field, value)
                  }
                  onEmployeeSelect={handleEmployeeSelect}
                  errors={errors}
                  disabled={saving}
                  canManageAch={!!companies?.manageAchData?.[companyUuid]}
                />
              );
            })
          )}
        </div>
      </div>
      <FormFooter
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={saving}
        saveLabel={saving ? "Saving..." : "Save User"}
      />
    </div>
  );
}
